import { useHistory, useLocation } from "react-router-dom";
import { z } from "zod";
import {
  ANNOUNCEMENT_QUERY_PARAM,
  ANNOUNCEMENT_SLUGS,
  toAnnouncementSlug,
  type AnnouncementSlug,
} from "../../../../api/src/shared/domain/announcement";
import { extractRoomIdFromPath } from "../../domains/room/utils/roomIdFromPath";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { createMemoryStore, createSessionStore } from "../../lib/store";
import { apiClient, useApiQuery } from "../../services/api";
import {
  gaEventAnnouncementCtaClicked,
  gaEventAnnouncementDismissed,
  gaEventAnnouncementViewed,
} from "../../tools/analytics/domains/announcement.analytics";
import { ANNOUNCEMENT_COMPONENTS } from "./registry";
import type { AnnouncementActionId, AnnouncementDismissMethod } from "./types";

/**
 * Every piece of client state below is keyed by the ACCOUNT as well as the
 * slug. Logging out is an in-tab navigation, so the same tab, the same session
 * storage and the same page memory serve the next account. Keyed by slug alone,
 * that account inherited the last one's closes, its confirmed seen writes and
 * the sheet it had on screen.
 */
type UserSlug = { userId: string; slug: AnnouncementSlug };

const userSlugListSchema = z.array(
  z.object({ userId: z.string(), slug: z.enum(ANNOUNCEMENT_SLUGS) })
);

/**
 * The announcements closed during THIS browser session.
 *
 * The server excludes what the reader has seen, so this covers the gap between
 * closing one and the next fetch of the schedule. It covers it for ALL of them
 * rather than for the last: with a single slug, closing a second pop-up un-hid
 * the first. Session storage rather than page memory, because the app reloads
 * itself mid-visit (the service worker update), and a reload before the fetch
 * landed would otherwise re-open what the reader just closed.
 */
const useClosedAnnouncements = createSessionStore<UserSlug[]>(
  "closed-announcements",
  [],
  userSlugListSchema
);

/**
 * The announcements whose "seen" write the server has CONFIRMED this session.
 *
 * The write is what makes a pop-up show once, so it gets a gate of its own and
 * not the analytics claim: that claim is spent the moment the impression fires,
 * whether or not the request behind it landed. This one is written on success
 * only, so a failed write is retried when the reader closes or acts, and a
 * successful one is never sent twice.
 */
const useSeenRecorded = createSessionStore<UserSlug[]>(
  "seen-announcements",
  [],
  userSlugListSchema
);

/**
 * The announcement on screen right now, and the schedule row it came from.
 *
 * The server marks a pop-up seen the moment it is shown, so the very next
 * fetch of the schedule no longer lists it, and the schedule refetches on every
 * window focus. Without this, a reader who left the app with the sheet open and
 * came back watched it vanish mid-read. Page memory on purpose: it holds the
 * sheet for as long as the page lives and no longer, and a reload starts from
 * what the server says.
 */
type Showing = {
  userId: string;
  slug: AnnouncementSlug;
  roomId: string | null;
};

const useShowingAnnouncement = createMemoryStore<Showing | null>(null);

/** Whether an announcement sheet is on screen: the JuisciGPT wizard waits for it (INV-03). */
export function useIsAnnouncementShowing(): boolean {
  const [showing] = useShowingAnnouncement();
  return showing !== null;
}

/**
 * Where an announcement MAY appear: an allow-list, not a deny-list.
 *
 * A global schedule reaches the public app, and the public app is two places:
 * the discovery tab and Juisci GPT. A room schedule reaches its room, and a
 * room is every page under `/room/.../:roomId` for THAT room. Nowhere else
 * shows anything: not the settings, not the profile, not another room's pages,
 * and not the flows a reader has to finish (auth, onboarding, signup), which
 * own the whole screen. The visualizer's preview page renders exactly one
 * announcement on purpose and is "elsewhere" like the rest.
 *
 * The first version listed the routes to avoid. That list reached every other
 * route by default, so an oncologist inside a client room met a public-app
 * announcement on the room's feed. Naming where a pop-up belongs cannot drift
 * that way.
 */
const PUBLIC_SURFACE_PATHS = ["/discovery", "/gpt"] as const;

export type AnnouncementSurface =
  { kind: "public" } | { kind: "room"; roomId: string } | { kind: "elsewhere" };

export function surfaceOf(pathname: string): AnnouncementSurface {
  const roomId = extractRoomIdFromPath(pathname);
  if (roomId !== null) return { kind: "room", roomId };
  const isPublic = PUBLIC_SURFACE_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
  return isPublic ? { kind: "public" } : { kind: "elsewhere" };
}

/** Whether a schedule row belongs on this surface. */
export function fitsSurface(
  row: { roomId: string | null },
  surface: AnnouncementSurface
): boolean {
  switch (surface.kind) {
    case "public":
      return row.roomId === null;
    case "room":
      return row.roomId === surface.roomId;
    case "elsewhere":
      return false;
    default:
      throw new Error(surface satisfies never);
  }
}

export type AnnouncementSelection = {
  slug: AnnouncementSlug;
  /** The room the SCHEDULE put it in, for the funnel. Null for a deep link. */
  roomId: string | null;
  /**
   * Whether the sheet is open. A closed one is still selected: the drawer needs
   * a render with `isOpen` false while it is mounted, or it vanishes instead of
   * sliding away. The next fetch of the schedule drops it a moment later.
   */
  open: boolean;
} | null;

/**
 * WHICH pop-up the reader sees, and whether it is open, from everything the
 * host knows. Pure, so the rules are testable without a router or a query:
 *
 * - Nobody is interrupted before they have an account, deep link included:
 *   every destination is behind the door, and the impression a shown pop-up
 *   records is a private write.
 * - Nothing shows on a surface that shows nothing.
 * - The URL wins. `?announcement=<slug>` is a push notification's deep link:
 *   the reader tapped a notification about THAT announcement, so it opens
 *   whether or not the schedule offers it and whether or not they closed it
 *   before. What closes it is the parameter going away, not the closed set.
 * - Otherwise the sheet already on screen stays on screen while it is open,
 *   even after the schedule stops listing it, and then the first scheduled row
 *   that belongs on this surface, one at a time: an interruption that stacks is
 *   two interruptions.
 */
export function selectAnnouncement(input: {
  signedIn: boolean;
  requested: AnnouncementSlug | null;
  surface: AnnouncementSurface;
  scheduled: ReadonlyArray<{ slug: AnnouncementSlug; roomId: string | null }>;
  showing: { slug: AnnouncementSlug; roomId: string | null } | null;
  closed: ReadonlyArray<AnnouncementSlug>;
}): AnnouncementSelection {
  const { signedIn, requested, surface, scheduled, showing, closed } = input;
  if (!signedIn) return null;
  if (surface.kind === "elsewhere") return null;
  if (requested !== null) {
    // A deep link has no schedule row of its own. It reports the room only
    // when a row for that very slug belongs here, never one borrowed from an
    // unrelated row.
    const row = scheduled.find(
      (candidate) =>
        candidate.slug === requested && fitsSurface(candidate, surface)
    );
    return {
      slug: requested,
      roomId: row === undefined ? null : row.roomId,
      open: true,
    };
  }
  const held =
    showing !== null &&
    !closed.includes(showing.slug) &&
    fitsSurface(showing, surface)
      ? showing
      : null;
  const row =
    held !== null
      ? held
      : scheduled.find((candidate) => fitsSurface(candidate, surface));
  if (row === undefined) return null;
  return {
    slug: row.slug,
    roomId: row.roomId,
    open: !closed.includes(row.slug),
  };
}

/**
 * Hosts the pop-up announcement the reader sees, records that they have seen
 * it, and records how they left it.
 *
 * Mounted ONCE beside the router, next to `DesktopAuthGateModal`, so it reaches
 * the reader wherever they are and no page has to know announcements exist.
 * `selectAnnouncement` above decides what shows. What the reader sees is a
 * component (`registry.ts`); nothing here is about what it says.
 */
export default function AnnouncementHost(): React.JSX.Element | null {
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const [closed, setClosed] = useClosedAnnouncements();
  const [seenRecorded, setSeenRecorded] = useSeenRecorded();
  const [showing, setShowing] = useShowingAnnouncement();

  const scheduledQuery = useApiQuery(
    "/webapp/private/users/me/announcements",
    currentUser === null ? false : {}
  );
  const scheduled =
    scheduledQuery.state === "success" ? scheduledQuery.body : [];

  const requestedParam = new URLSearchParams(location.search).get(
    ANNOUNCEMENT_QUERY_PARAM
  );
  const requested =
    requestedParam === null ? null : toAnnouncementSlug(requestedParam);

  // This account's slice of each store. Another account's closes, writes and
  // held sheet are not this reader's.
  const userId = currentUser === null ? null : currentUser.uid;
  const closedHere = closed
    .filter((entry) => entry.userId === userId)
    .map((entry) => entry.slug);
  const showingHere =
    showing !== null && showing.userId === userId ? showing : null;

  const selection = selectAnnouncement({
    signedIn: userId !== null,
    requested,
    surface: surfaceOf(location.pathname),
    scheduled,
    showing: showingHere,
    closed: closedHere,
  });

  // The server's record that the reader has SEEN it, which is what "shown
  // once" hangs on. It used to be written on close or on the CTA, and the CTA
  // leaves the app for another site: a webview suspended or reloaded before
  // that write landed brought the pop-up back on the next boot. Written the
  // moment it is shown instead, while the reader is looking at it and nothing
  // is leaving the page, and again on close or act until it has landed. A
  // failure is not caught twice: the api client reports every request
  // error to Sentry with the classification it deserves, and the reader gets
  // no toast for a write they never asked for.
  async function recordSeen(
    userId: string,
    slug: AnnouncementSlug
  ): Promise<void> {
    const recorded = (entries: readonly UserSlug[]): boolean =>
      entries.some((entry) => entry.userId === userId && entry.slug === slug);
    if (recorded(seenRecorded)) return;
    try {
      await apiClient.post(
        "/webapp/private/users/me/announcements/:slug/seen",
        { param: { slug } }
      );
    } catch {
      return;
    }
    setSeenRecorded((previous) =>
      recorded(previous) ? previous : [...previous, { userId, slug }]
    );
  }

  // Once per session per slug, which is the right scope for an interruption: a
  // reader who bounces between two surfaces with the same announcement up saw it
  // once. `hidden` covers both "nothing is scheduled" and "it is closing", so a
  // dismissal still in flight cannot count a second impression.
  useTrackOnMount(
    "announcement-shown",
    selection !== null && selection.open
      ? { status: "shown", key: selection.slug }
      : { status: "hidden" },
    () => {
      if (selection === null || userId === null)
        throw new Error("should not happen: impression with no announcement");
      gaEventAnnouncementViewed({
        slug: selection.slug,
        roomId: selection.roomId,
      });
      setShowing({ userId, slug: selection.slug, roomId: selection.roomId });
      void recordSeen(userId, selection.slug);
    }
  );

  // The reader is done with it. The closed set hides it at once; the deep
  // link's parameter has to go too, and that removal is what closes a
  // deep-linked sheet: left in the URL it would re-open the announcement on the
  // very next render, and on a reload or a back button. `replace` rather than
  // `push`, so going back does not land the reader on the announcement again.
  // Then the seen write, if the impression's did not land, and a refetch of the
  // schedule so the row goes and the next announcement, if any, gets its turn.
  async function close(userId: string, slug: AnnouncementSlug): Promise<void> {
    setClosed((previous) =>
      previous.some((entry) => entry.userId === userId && entry.slug === slug)
        ? previous
        : [...previous, { userId, slug }]
    );
    setShowing(null);
    if (requested !== null) {
      const params = new URLSearchParams(location.search);
      params.delete(ANNOUNCEMENT_QUERY_PARAM);
      const query = params.toString();
      history.replace(
        query === "" ? location.pathname : `${location.pathname}?${query}`
      );
    }
    await recordSeen(userId, slug);
    await scheduledQuery.refetch();
  }

  if (selection === null) return null;
  if (userId === null)
    throw new Error("should not happen: a selection without a signed-in user");

  const { slug, roomId, open } = selection;
  const Announcement = ANNOUNCEMENT_COMPONENTS[slug];

  return (
    <Announcement
      isOpen={open}
      onDismiss={(method: AnnouncementDismissMethod) => {
        gaEventAnnouncementDismissed({ slug, roomId, method });
        void close(userId, slug);
      }}
      onAct={(action: AnnouncementActionId) => {
        // Acting ends the announcement too: the reader has dealt with the
        // message, and showing it again after they acted on it would be the
        // worst version of this feature.
        gaEventAnnouncementCtaClicked({ slug, roomId, action });
        void close(userId, slug);
      }}
    />
  );
}
